import { appNowMs } from '../../../../core/appTime';
import { Alert } from '../../../../components/ui';
import { unlistedCancellationText } from '../../../../utils/listingHold';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { HandoverJobCard } from '../../../../components/HandoverJobCard';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faComments, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useCancelBookingMutation } from '../../../../services/bookingService/__generated__/queries.generated';
import {
  HOLD_SECURITY_DEPOSIT,
  RELEASE_SECURITY_DEPOSIT,
  GET_BOOKING_DETAILS,
  REMOVE_BOOKING_PENALTIES,
  UPDATE_BOOKING_MINIMUM_RENTAL_DAYS,
  APPROVE_CAR_SWAP,
  REJECT_CAR_SWAP,
  FORCE_APPROVE_CAR_SWAP,
  FORCE_DECLINE_CAR_SWAP,
  GET_BOOKING_AS_GUEST,
  GET_BOOKING_AS_HOST,
  GET_BOOKING_HANDOVER,
  GET_BOOKING_ACTIVITIES,
  UPDATE_ACTUAL_RETURN_DATE_TIME,
  REQUEST_ADD_ADDITIONAL_DRIVER,
} from '../../../../services/bookingService/queries';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '../../../../components/button';
import { toast } from 'react-toastify';
import type { IUser } from '../../../../../__generated__/graphql';
import {
  BookingStatus,
  CancellationReason,
  InvoiceType,
  CarSwapStatus,
} from '../../../../../__generated__/graphql';
import { ModifyBookingModal } from '../../../../components/ModifyBookingModal';
import { useState, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { DateTime } from 'luxon';
import { URLS } from '../../../../../constants/urls';
import { roleStore } from '../../../../../store/roleStore';
import { Role } from '../../../../../store/roleStore';
import { useSnapshot } from 'valtio';
import { GET_CAR } from '../../../../services/carService/queries';
import { ProtectionPlanDetails } from '../../../../components/protectionPlan/protectionPlanDetails';
import { ReceiptDetailsModal } from '../../../../components/receipt/receiptDetailsModal';
import { getBookingReceiptDisplayTotal } from '../../../../helpers/bookingReceiptDisplayTotal';
import { StartTripCheckinModal } from '../../../../components/StartTripCheckinModal';
import { SwapHistoryModal } from '../../../../components/SwapHistoryModal';
import { BookingActivityTimeline } from '../../../../components/BookingActivityTimeline';
import { TripPhotosModal } from '../../../../components/TripPhotosModal';
import { PostTripReturnModal } from '../../../../components/PostTripReturnModal';
import { IncidentChargeModal } from '../../../../components/IncidentChargeModal';
import {
  useCreateRefundInvoiceMutation,
  useRemoveInvoiceMutation,
  useRefreshInvoiceRefundStatusMutation,
} from '../../../../services/invoiceServices/__generated__/queries.generated';
import { toastActions } from '../../../../../store/toastStore';
import { useCalculateTripExtensionMutation } from '../../../../services/bookingService/__generated__/queries.generated';
import ExtendTripPaymentModal from '../../../../components/ExtendTripPaymentModal';
import { OutstandingCharges } from '../../../../components/OutstandingCharges';
import BookingDetailsSkeleton from '../../../../components/skeletons/BookingDetailsSkeleton';
import {
  calendarJsDateFromMillisInZone,
  clockTimeInZone,
  convert24To12Hour,
  formatInstantInCarTimezone,
} from '../../../../helpers/timezoneHelpers';
import { Modal } from '../../../../components/modal';
import chatService from '../../../../services/chatService/ChatService';
import { authStore } from '../../../../../store/authStore';
import { CarSwapModal } from '../../../../components/CarSwapModal';
import { Colors, DesignTokens } from '../../../../styles/colors';
import { RateTripButton } from '../../../../components/ratings';
import DamageClaimModal from '../../../../components/DamageClaimModal';
import { useGetDamageClaimsByBookingForServiceQuery } from '../../../../services/damageClaimService/__generated__/queries.generated';
import { gql } from '@apollo/client';
import { formatShortId } from '../../../../utils/short-id';
import { DLVerification } from '../../../../components/DLVerification';
import { AdminOnlyNote, ManagedByBrandNote } from '../../../../components/ManagedByBrandNote';
import { useHostCapabilities } from '../../../../hooks/useHostCapabilities';
import { useBookingLocale } from '../../../../hooks/useBookingLocale';
import { tripAllowanceDisplay } from '../../../../utils/tripDistance';
import type { DistanceUnit } from '../../../../utils/units';
import NumericInput from '../../../../components/NumericInput';
import { SCREENS, SCREENS_MAX } from '../../../../components/responsive';
import {
  buildGoogleMapsDirectionsUrl,
  formatBookingLocationDisplay,
} from '../../../../helpers/formatAddress';

const CAN_REPORT_DAMAGE = gql`
  query CanReportDamage($bookingId: String!) {
    canReportDamage(bookingId: $bookingId) {
      canReport
      reason
      deadline
    }
  }
`;

const Container = styled.div`
  ${tw`w-full max-w-screen-md mx-auto flex flex-col box-border px-4 md:px-6 py-6`}
`;

// Mobile header
const MobileHeader = styled.div`
  ${tw`w-full hidden`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`flex flex-col mb-2 block`}
  }
`;

const BookingTitle = styled.h1`
  ${tw`text-center font-normal`}
  font-size: ${DesignTokens.typography.fontSize.body};
  line-height: ${DesignTokens.typography.lineHeight.body};
`;

// Reusable DivideLine component
const DivideLine = styled.div<{ showMobile?: boolean }>`
  ${tw`w-full h-px bg-bg-secondary my-4`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${props => (props.showMobile ? tw`block` : tw`hidden`)}
  }
`;

const Header = styled.div`
  ${tw`flex items-center justify-between pb-2`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`hidden`}
  }
`;

const HeaderLeft = styled.div`
  ${tw`flex flex-col`}
`;

const TripLabel = styled.div`
  ${tw`text-sm text-text-secondary mb-1`}
`;

const Title = styled.h1`
  font-family: ${DesignTokens.typography.fontFamilyDisplay};
  font-size: ${DesignTokens.typography.fontSize.title1};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  line-height: ${DesignTokens.typography.lineHeight.title1};

  @media (min-width: ${SCREENS.md}) {
    font-size: ${DesignTokens.typography.fontSize.title1Md};
  }
`;

const ShortIdBadge = styled.span`
  ${tw`px-2 py-0.5 rounded-full text-xs font-semibold inline-flex items-center uppercase`}
  background-color: ${Colors.Gray100};
  color: ${Colors.Black};
`;

const CarPreview = styled.div`
  ${tw`flex items-center gap-4 mb-4`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`border-b border-border-default pb-4`}
  }
`;

const CarInfo = styled.div`
  ${tw`flex flex-col`}
`;

const CarName = styled.div`
  ${tw`text-base font-medium`}
`;

const ViewDetails = styled.a`
  ${tw`text-sm underline`}
  color: ${Colors.BlackLight};

  &:hover {
    color: ${Colors.Black};
  }
`;

const CarImage = styled.img`
  ${tw`w-16 h-16 rounded-md object-cover`}
`;

const TripSection = styled.div`
  ${tw`mb-8`}
`;

const TripDates = styled.div`
  ${tw`flex items-center gap-6 mb-6`}
`;

const DateBlock = styled.div`
  ${tw`flex flex-col`}
`;

const DateText = styled.div`
  ${tw`text-base font-medium`}
`;

const TimeText = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const ArrowIcon = styled.span`
  ${tw`text-text-tertiary`}
`;

const LocationSection = styled.div`
  ${tw`mb-6`}
`;

const LocationAddress = styled.div`
  ${tw`text-base mb-1`}
`;

const LocationAddressLink = styled.a`
  ${tw`text-sm underline block`}
  color: ${Colors.PrimaryBlueDark};

  &:hover {
    color: ${Colors.Blue700};
  }
`;

const InfoSection = styled.div`
  ${tw`mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-text-primary mb-2`}
  font-family: ${DesignTokens.typography.fontFamilyDisplay};
  font-size: ${DesignTokens.typography.fontSize.title3};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.title3};
`;

const InfoText = styled.div`
  ${tw`text-base mb-1`}
`;

const SubText = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const DetailLink = styled.a`
  ${tw`text-base font-semibold cursor-pointer underline`}
  color: ${Colors.BlackLight};

  &:hover {
    color: ${Colors.Black};
  }
`;

const GuestSection = styled.div`
  ${tw`mt-8`}
`;

const GuestInfo = styled.div`
  ${tw`flex items-center gap-4 mb-4`}
`;

const Avatar = styled.div`
  ${tw`w-10 h-10 rounded-full bg-bg-secondary flex items-center justify-center text-text-secondary`}
`;

const GuestDetails = styled.div`
  ${tw`flex flex-col`}
`;

const GuestName = styled.div`
  ${tw`font-medium`}
`;

const JoinDate = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const ButtonContainer = styled.div`
  ${tw`flex gap-2 w-full flex-col items-center max-w-sm mx-auto`}
`;

const ContactModalContent = styled.div`
  ${tw`flex flex-col gap-3 w-full max-w-xs mx-auto`}
`;

const ContactModalTitle = styled.h3`
  font-family: ${DesignTokens.typography.fontFamilyDisplay};
  font-size: ${DesignTokens.typography.fontSize.title3};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.title3};
  text-align: center;
  margin-bottom: 4px;
`;

const ContactModalItem = styled.a`
  display: block;
  width: 100%;
  text-decoration: none;
`;

const FixedBottomBar = styled.div`
  ${tw`fixed bottom-0 left-0 right-0 bg-white py-4 px-4 z-50`}
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
`;

const FixedBottomBarInner = styled.div`
  ${tw`w-full max-w-sm mx-auto`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`max-w-full`}
  }
`;

const CardNumber = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

// Add this styled component for the strikethrough text
const CancelledDateText = styled(DateText)`
  ${tw`line-through text-text-secondary`}
`;

const AddDriverSection = styled.div`
  ${tw`mt-2 mb-2 flex justify-center w-full max-w-sm mx-auto sm:mx-0`}
`;

const StandaloneButtonWrapper = styled.div`
  ${tw`flex justify-center w-full max-w-sm mx-auto sm:mx-0 my-2`}
`;

const DriverInput = styled.input`
  ${tw`w-[300px] p-2  box-border rounded-md mb-2 h-[44px] border border-border-default`}
`;

const SecurityDepositSection = styled.div`
  ${tw`mb-6 p-4 border border-border-default rounded-lg`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`block p-3`}
  }
`;

const TripPhotosSection = styled.div`
  ${tw`mb-6 p-4 border border-border-default rounded-lg`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`block p-3`}
  }
`;

const TripPhotosLink = styled.a`
  ${tw`text-sm cursor-pointer underline`}
  color: ${Colors.BlackLight};

  &:hover {
    color: ${Colors.Black};
  }
`;

// New card components for improved layout
const Card = styled.div`
  ${tw`mb-6 p-6 bg-white border border-border-default rounded-lg shadow-card`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`p-4`}
  }
`;

const StatusBanner = styled.div<{ status?: string }>`
  ${tw`flex items-center gap-3 p-4 rounded-t-lg mb-4 -mx-6 -mt-6`}
  background-color: ${props => {
    switch (props.status) {
      case 'CONFIRMED':
      case 'BOOKED':
        return Colors.Success;
      case 'IN_PROGRESS':
        return Colors.Black;
      case 'COMPLETED':
        return Colors.Gray500;
      case 'CANCELLED':
        return Colors.Error;
      case 'PENDING':
        return Colors.Warning;
      default:
        return Colors.Success;
    }
  }};
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`-mx-4 -mt-4`}
  }
`;

const StatusBannerText = styled.div`
  ${tw`flex items-center gap-2 text-white font-semibold text-base`}
`;

const StatusBadgeText = styled.span`
  ${tw`uppercase`}
`;

const CheckinLabel = styled.span`
  ${tw`flex items-center gap-2 text-white text-sm`}
`;

const CardTitle = styled.h2`
  ${tw`text-text-primary mb-4`}
  font-family: ${DesignTokens.typography.fontFamilyDisplay};
  font-size: ${DesignTokens.typography.fontSize.title3};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.title3};

  @media (min-width: ${SCREENS.md}) {
    font-size: ${DesignTokens.typography.fontSize.title3Md};
  }
`;

const CardContent = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const TwoColumnLayout = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-6`}
`;

const Column = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const Label = styled.span`
  ${tw`text-sm text-text-secondary mb-1`}
`;

const Value = styled.span`
  ${tw`text-base font-medium text-text-primary`}
`;

const ValueLink = styled.a`
  ${tw`text-base font-medium underline`}
  color: ${Colors.BlackLight};

  &:hover {
    color: ${Colors.Black};
  }
`;

const InfoRow = styled.div`
  ${tw`flex justify-between items-center py-2 border-b border-border-default last:border-b-0`}
`;

const InfoRowLeft = styled.div`
  ${tw`flex flex-col`}
`;

const InfoRowLabel = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const InfoRowValue = styled.span`
  ${tw`text-base font-medium text-text-primary`}
`;

const PeopleSection = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const PersonInfo = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const PersonName = styled.div`
  ${tw`font-medium text-base text-text-primary`}
`;

const PersonEmail = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const PersonMeta = styled.div`
  ${tw`text-xs text-text-secondary`}
`;

const RefundSection = styled.div`
  ${tw`mb-6 p-4 rounded-lg`}
  background-color: ${Colors.BgSecondary};
  border: 1px solid ${Colors.Gray200};
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`block p-3`}
  }
`;

const ActivityTimelineSection = styled.div`
  ${tw`mb-6 p-4 border border-border-default rounded-lg bg-bg-secondary`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`block p-3`}
  }
`;

const DepositRow = styled.div`
  ${tw`flex justify-between items-center py-2`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`flex-col items-start gap-1`}
  }
`;

const DepositLabel = styled.span`
  ${tw`text-text-secondary text-sm`}
`;

const DepositValue = styled.span`
  ${tw`font-medium text-sm`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`text-left`}
  }
`;

const DepositDivider = styled.div`
  ${tw`border-t border-border-default my-4`}
`;

const DepositButtonContainer = styled.div`
  ${tw`flex gap-4 mt-4 justify-center`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`flex-col gap-2 items-stretch`}
  }
`;

const StatusBadge = styled.span<{ color: string }>`
  ${tw`px-2 py-1 rounded-md text-xs font-medium`}
  background-color: ${props => props.color}20;
  color: ${props => props.color};
`;

const DamageClaimsSection = styled.div`
  ${tw`mb-6 p-4 border border-orange-200 rounded-lg`}
  background-color: #FFF7ED;
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`block p-3`}
  }
`;

const ClaimCardLink = styled(Link)`
  ${tw`block bg-white rounded-lg p-3 border border-orange-100 mb-2 last:mb-0 no-underline text-inherit hover:border-pink-300 transition-colors cursor-pointer`}
`;

const ClaimNumHighlight = styled.span`
  ${tw`text-black font-medium`}
`;
const ReleaseModalContent = styled.div`
  ${tw`flex flex-col gap-4 w-full box-border`}
`;

const ReleaseInput = styled.input`
  ${tw`w-full p-2 box-border rounded-md mb-2 h-[44px] border border-border-default`}
`;

const ReasonInput = styled.textarea`
  ${tw`w-full p-2 box-border rounded-md mb-2 min-h-[80px] border border-border-default`}
`;

const ReleaseHelperText = styled.p`
  ${tw`text-sm text-text-secondary mt-1`}
`;

const RefreshLink = styled.button`
  ${tw`text-xs bg-transparent border-none cursor-pointer hover:underline p-0 ml-2`}
  color: ${Colors.BlackLight};
  &:disabled {
    ${tw`cursor-not-allowed`}
    color: ${Colors.Gray400};
  }
`;

const SwapRequestBanner = styled.div`
  ${tw`mb-6 p-4 border-2 rounded-lg`}
  border-color: ${Colors.Gray300};
  background-color: ${Colors.BgSecondary};
`;

const SwapRequestTitle = styled.h3`
  ${tw`mb-2`}
  font-family: ${DesignTokens.typography.fontFamilyDisplay};
  font-size: ${DesignTokens.typography.fontSize.title3};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.title3};
  color: ${Colors.TextPrimary};
`;

const SwapRequestText = styled.p`
  ${tw`text-sm text-text-secondary mb-3`}
`;

const SwapRequestActions = styled.div`
  ${tw`flex gap-4 mt-4`}
`;

// First, add this styled component for the modal content
const AddDriverModalContent = styled.div`
  ${tw`flex flex-col gap-4 w-full box-border`}
`;

// Spec 028: in the trip's unit (shared with the receipt modal).
const getMileageIncludedDisplay = (booking: any, unit: DistanceUnit) =>
  tripAllowanceDisplay(booking, unit, { skipEmptyFallback: true });

// Mobile content container
const MobileContent = styled.div`
  ${tw`hidden`}
  @media (max-width: ${SCREENS_MAX.md}) {
    ${tw`block`}
  }
`;

export default function BookingDetails() {
  const { role } = useSnapshot(roleStore);
  const auth = useSnapshot(authStore);
  const isHost = role === Role.HOST;
  // Spec 014a: the trip opens only on its own brand's site, so these are the trip brand's switches.
  // 014b: the brand's admin still sees switched-off actions, marked "Admin only".
  const { visible: canHost, adminOnly } = useHostCapabilities();
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if this is a guest URL (e.g., /cars/guest/booking/:id)
  const isGuestUrl = location.pathname.includes('/cars/guest/booking');

  // Use appropriate query based on URL - backend will validate ownership
  const {
    data,
    loading,
    error,
    refetch: refetchBooking,
  } = useQuery(isGuestUrl ? GET_BOOKING_AS_GUEST : GET_BOOKING_AS_HOST, {
    variables: { id: bookingId ?? '' },
    skip: !bookingId,
    fetchPolicy: 'network-only',
  });

  // Extract booking from the response (field name varies based on query)
  const booking = isGuestUrl ? data?.getBookingAsGuest : data?.getBookingAsHost;
  // Spec 028: the unit and currency this trip was booked in.
  const bookingLocale = useBookingLocale(bookingId, isGuestUrl ? 'guest' : 'host');

  // Spec 019 T067: the host's handover job, apart from the trip so an older API cannot break the page.
  const { data: handoverData } = useQuery(GET_BOOKING_HANDOVER, {
    variables: { id: bookingId ?? '' },
    skip: !bookingId || isGuestUrl,
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  });
  const handover = handoverData?.getBookingAsHost;

  const receiptDisplayTotal = getBookingReceiptDisplayTotal(booking?.receipt, booking?.invoices);
  const mileageIncludedDisplay = getMileageIncludedDisplay(booking, bookingLocale.distanceUnit);

  // Handle errors from backend (e.g., ForbiddenException when user doesn't own the booking)
  useEffect(() => {
    if (error) {
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message;
      toast.error(errorMessage);
      navigate('/', { replace: true });
    }
  }, [error, navigate]);

  // Auto-open DL verification modal for host when guest submits DL
  const prevDLStatus = useRef(booking?.dlVerificationStatus);
  useEffect(() => {
    if (
      isHost &&
      canHost('licenceReview') &&
      booking?.dlVerificationStatus === 'SUBMITTED' &&
      prevDLStatus.current !== 'SUBMITTED'
    ) {
      setAutoOpenDLVerify(true);
      toast.info('Guest has submitted their DL verification. Please review.');
    }
    prevDLStatus.current = booking?.dlVerificationStatus;
  }, [booking?.dlVerificationStatus, isHost, canHost]);

  // Fetch swap-requested car details (only when a swap is pending)
  const swapRequestedCarId = (booking as any)?.swapRequestedCarId;
  const { data: swapCarData } = useQuery(GET_CAR, {
    variables: { id: swapRequestedCarId ?? '' },
    skip: !swapRequestedCarId,
  });

  // Check if damage can be reported for this booking
  const { data: canReportDamageData } = useQuery(CAN_REPORT_DAMAGE, {
    variables: { bookingId: bookingId ?? '' },
    skip: !bookingId,
  });

  // Resolve pickup/return location objects for display and directions
  const resolveBookingLocation = (locationId: number | null | undefined, isPickup: boolean) => {
    if (!booking?.car?.location) {
      return null;
    }

    if (!locationId || locationId === booking.car.location.id) {
      return booking.car.location;
    }

    return isPickup ? booking.pickupLocation : booking.returnLocation;
  };

  const pickupLocationDisplay = formatBookingLocationDisplay(
    resolveBookingLocation(booking?.pickupLocationId, true),
    booking?.car?.location,
  );
  const returnLocationDisplay = formatBookingLocationDisplay(
    resolveBookingLocation(booking?.returnLocationId, false),
    booking?.car?.location,
  );
  const pickupReturnSameLocation =
    (booking?.pickupLocationId != null &&
      booking?.returnLocationId != null &&
      String(booking.pickupLocationId) === String(booking.returnLocationId)) ||
    pickupLocationDisplay === returnLocationDisplay;

  const { data: bookingActivitiesData, refetch: refetchBookingActivities } = useQuery(
    GET_BOOKING_ACTIVITIES,
    {
      variables: { bookingId: bookingId ?? '' },
      skip: !bookingId,
    },
  );

  // Fetch damage claims for this booking
  const { data: damageClaimsData, refetch: refetchDamageClaimsByBooking } =
    useGetDamageClaimsByBookingForServiceQuery({
      variables: { bookingId: bookingId ?? '' },
      skip: !bookingId,
    });

  const damageClaims = damageClaimsData?.damageClaimsByBooking || [];

  const [cancelBooking, { loading: cancelBookingLoading }] = useCancelBookingMutation({
    refetchQueries: [
      'GetMyBookings',
      'GetBookingsForMyListings',
      'GetCancelledBookingsAsGuest',
      'GetCancelledBookingsAsHost',
      ...(bookingId ? [{ query: GET_BOOKING_ACTIVITIES, variables: { bookingId } }] : []),
    ],
    onCompleted: () => {
      toast.success('Booking cancelled successfully');
      refetchBooking();
      refetchBookingActivities();
    },
  });

  const [removeInvoice] = useRemoveInvoiceMutation();

  const [refreshRefundStatus, { loading: refreshingRefundStatus }] =
    useRefreshInvoiceRefundStatusMutation({
      onCompleted: () => {
        toast.success('Refund status refreshed');
        refetchBooking();
      },
      onError: error => {
        toast.error(`Failed to refresh status: ${error.message}`);
      },
    });

  // Car swap mutations
  const swapRefetchQueries = bookingId
    ? [
        { query: GET_BOOKING_DETAILS, variables: { id: bookingId } },
        { query: GET_BOOKING_ACTIVITIES, variables: { bookingId } },
      ]
    : [];

  const [approveCarSwap, { loading: approveSwapLoading }] = useMutation(APPROVE_CAR_SWAP, {
    refetchQueries: swapRefetchQueries,
    onCompleted: () => {
      toast.success('Car swap approved successfully');
      refetchBooking();
      refetchBookingActivities();
    },
    onError: error => {
      toast.error(error.message || 'Failed to approve car swap');
    },
  });

  const [rejectCarSwap, { loading: rejectSwapLoading }] = useMutation(REJECT_CAR_SWAP, {
    refetchQueries: swapRefetchQueries,
    onCompleted: () => {
      toast.success('Car swap rejected');
      refetchBooking();
      refetchBookingActivities();
    },
    onError: error => {
      toast.error(error.message || 'Failed to reject car swap');
    },
  });

  const [forceApproveCarSwap, { loading: forceApproveSwapLoading }] = useMutation(
    FORCE_APPROVE_CAR_SWAP,
    {
      refetchQueries: swapRefetchQueries,
      onCompleted: () => {
        toast.success('Car swap force approved successfully');
        refetchBooking();
        refetchBookingActivities();
      },
      onError: error => {
        toast.error(error.message || 'Failed to force approve car swap');
      },
    },
  );

  const [forceDeclineCarSwap, { loading: forceDeclineSwapLoading }] = useMutation(
    FORCE_DECLINE_CAR_SWAP,
    {
      refetchQueries: swapRefetchQueries,
      onCompleted: () => {
        toast.success('Car swap force declined');
        refetchBooking();
        refetchBookingActivities();
      },
      onError: error => {
        toast.error(error.message || 'Failed to force decline car swap');
      },
    },
  );

  const handleApproveSwap = () => {
    if (!bookingId) return;
    approveCarSwap({
      variables: {
        input: {
          bookingId,
        },
      },
    });
  };

  const handleRejectSwap = () => {
    if (!bookingId) return;
    rejectCarSwap({
      variables: {
        input: {
          bookingId,
        },
      },
    });
  };

  const handleForceApproveSwap = () => {
    if (!bookingId) return;
    if (
      !window.confirm(
        'Are you sure you want to force approve this car swap? This will immediately complete the swap without guest approval.',
      )
    ) {
      return;
    }
    forceApproveCarSwap({
      variables: {
        input: {
          bookingId,
        },
      },
    });
  };

  const handleForceDeclineSwap = () => {
    if (!bookingId) return;
    if (
      !window.confirm(
        'Are you sure you want to force decline this car swap? This will cancel the swap request.',
      )
    ) {
      return;
    }
    forceDeclineCarSwap({
      variables: {
        input: {
          bookingId,
        },
      },
    });
  };

  const [createRefundInvoice, { loading: createRefundInvoiceLoading }] =
    useCreateRefundInvoiceMutation({
      onCompleted: ({ createRefundInvoice }) => {
        const isFullyRefunded = createRefundInvoice.type === InvoiceType.FullRefund;
        const penaltyAmount = createRefundInvoice.breakdown.penaltyAmount ?? 0;
        const hasServiceFee = isFullyRefunded && penaltyAmount > 0;

        // For full refunds with service fee, show the service fee message from notes
        // For full refunds without service fee, show "You will get a full refund"
        // For partial refunds, show the penalty notes
        let refundTypeMessage = '';
        if (isFullyRefunded) {
          if (hasServiceFee && createRefundInvoice.breakdown.notes) {
            refundTypeMessage = createRefundInvoice.breakdown.notes;
          } else {
            refundTypeMessage = 'You will get a full refund.';
          }
        } else {
          refundTypeMessage = createRefundInvoice.breakdown.notes ?? '';
        }

        const refundAmountMessage = `You will get a refund of $${(0 - Number(createRefundInvoice.amount)).toFixed(2)}.`;

        // Check if security deposit will be refunded
        // Use the same booking variable from outer scope (extracted from correct query field)
        const bookingData = isGuestUrl ? data?.getBookingAsGuest : data?.getBookingAsHost;
        const depositAmount =
          bookingData?.securityDepositAmount || bookingData?.receipt?.securityDeposit || 0;
        const hasDeposit = depositAmount > 0;
        const depositStatus = bookingData?.securityDepositStatus;
        const isDepositReleased =
          depositStatus === 'RELEASED' || depositStatus === 'PARTIALLY_RELEASED';
        // Deposit will be refunded if it exists and hasn't been released yet
        const depositWillBeRefunded = hasDeposit && !isDepositReleased;

        // Build refund message
        let refundMessage = `${refundTypeMessage} ${refundAmountMessage}`;

        // Add deposit refund information
        if (depositWillBeRefunded) {
          refundMessage += `\n\nA full deposit of $${depositAmount.toFixed(2)} will also be automatically refunded to your payment method.`;
        }

        const confirmMessage = `Are you sure you want to cancel the trip?`;

        toastActions.showToastAlert(
          'Confirm Refund', // Title
          refundMessage, // Message
          `${confirmMessage}`,
          () => {
            // OK button callback - proceed with cancellation
            // Use different status and reason based on whether it's host or guest
            const bookingStatus = isHost
              ? BookingStatus.CancelledByHost
              : isFullyRefunded
                ? BookingStatus.CancelledByGuestMoreThan_24Hours
                : BookingStatus.CancelledByGuestLessThan_24HoursBeforeTripStart;

            const cancellationReason = isHost
              ? CancellationReason.HostCancelled
              : CancellationReason.GuestRequested;

            cancelBooking({
              variables: {
                bookingId: bookingData?.id ?? '',
                bookingStatus,
                balance: createRefundInvoice.amount,
                cancellationReason,
                cancelledInitiatedAt: createRefundInvoice.createdAt,
                isCancellationPenaltyApplied: false,
                invoiceId: createRefundInvoice.id,
                cancellationPenalty: penaltyAmount,
              },
            });
          },
          () => {
            // Cancel button callback - do nothing or handle cancellation
            // Todo: delete the refund invoice just created
            removeInvoice({
              variables: {
                id: createRefundInvoice.id,
              },
            });
            toast.info('Booking not cancelled');
          },
        );
      },
      onError: (e: Error) => {
        toast.error(e.message || 'Failed to cancel trip');
      },
    });

  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [isStartTripModalOpen, setIsStartTripModalOpen] = useState(false);
  const [modifyCost, setModifyCost] = useState(0);
  const [modifyEndAt, setModifyEndAt] = useState(0);
  const [shortenTripPenalty, setShortenTripPenalty] = useState(0);
  const [freeCancellationServiceFee, setFreeCancellationServiceFee] = useState(0);
  const [isExtendTripPaymentModalOpen, setIsExtendTripPaymentModalOpen] = useState(false);
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [isSwapHistoryModalOpen, setIsSwapHistoryModalOpen] = useState(false);
  const [isDamageClaimModalOpen, setIsDamageClaimModalOpen] = useState(false);
  const [isTripPhotosModalOpen, setIsTripPhotosModalOpen] = useState(false);
  const [isPostTripReturnModalOpen, setIsPostTripReturnModalOpen] = useState(false);
  const [isIncidentChargeModalOpen, setIsIncidentChargeModalOpen] = useState(false);
  const [isActualReturnModalOpen, setIsActualReturnModalOpen] = useState(false);
  const [actualReturnValue, setActualReturnValue] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const showReceiptModal = () => {
    setIsReceiptModalOpen(true);
  };

  const [calculateTripExtension, { loading: calculateTripExtensionLoading }] =
    useCalculateTripExtensionMutation({
      onCompleted: async ({ calculateTripExtension }) => {
        if (!booking?.id) return toast.error('Booking not found');
        // The server falls back to the deposit card when no preferred card is recorded.
        if (!booking?.defaultPaymentMethodId && !booking?.securityDepositPaymentMethodId) {
          return toast.error('No payment method found');
        }
        setModifyCost(calculateTripExtension.priceDifference);
        setShortenTripPenalty(calculateTripExtension.shortenTripPenalty ?? 0);
        setFreeCancellationServiceFee(calculateTripExtension.freeCancellationServiceFee ?? 0);
        setModifyEndAt(calculateTripExtension.booking.endAt);
        setIsExtendTripPaymentModalOpen(true);
      },
      onError: (e: any) => {
        // Extract error message from GraphQL error
        const errorMessage =
          e?.graphQLErrors?.[0]?.message || e?.message || 'Failed to confirm trip extension';
        toast.error(errorMessage);
      },
    });

  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);

  const [showAddDriver, setShowAddDriver] = useState(false);
  const [driverEmail, setDriverEmail] = useState('');
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const [releaseAmount, setReleaseAmount] = useState<number>(0);
  const [releaseReason, setReleaseReason] = useState('');
  const [showMinRentalDaysModal, setShowMinRentalDaysModal] = useState(false);
  const [autoOpenDLVerify, setAutoOpenDLVerify] = useState(false);
  const [newMinRentalDays, setNewMinRentalDays] = useState<string>('');

  const [holdDeposit, { loading: holdingDeposit }] = useMutation(HOLD_SECURITY_DEPOSIT, {
    refetchQueries: [{ query: GET_BOOKING_DETAILS, variables: { id: bookingId } }],
    onCompleted: () => {
      toast.success('Deposit hold requested successfully');
      refetchBooking();
    },
    onError: error => {
      toast.error(`Failed to hold deposit: ${error.message}`);
    },
  });

  const [releaseDeposit, { loading: releasingDeposit }] = useMutation(RELEASE_SECURITY_DEPOSIT, {
    refetchQueries: [{ query: GET_BOOKING_DETAILS, variables: { id: bookingId } }],
    onCompleted: () => {
      toast.success('Deposit released successfully');
      setIsReleaseModalOpen(false);
      setReleaseAmount(0);
      setReleaseReason('');
      refetchBooking();
    },
    onError: error => {
      toast.error(`Failed to release deposit: ${error.message}`);
    },
  });

  const [updateActualReturnDateTime, { loading: updatingActualReturn }] = useMutation(
    UPDATE_ACTUAL_RETURN_DATE_TIME,
    {
      onCompleted: () => {
        toast.success('Actual return updated');
        setIsActualReturnModalOpen(false);
        refetchBooking();
        refetchBookingActivities();
      },
      onError: error => {
        toast.error(error.message || 'Failed to update actual return');
      },
    },
  );

  const handleHoldDeposit = () => {
    if (!booking?.id) return;
    holdDeposit({
      variables: { bookingId: booking.id, reason: holdReason.trim() || undefined },
    });
    setIsHoldModalOpen(false);
    setHoldReason('');
  };

  // Remove booking penalties mutation
  const [removeBookingPenalties, { loading: removePenaltiesLoading }] = useMutation(
    REMOVE_BOOKING_PENALTIES,
    {
      refetchQueries: [{ query: GET_BOOKING_DETAILS, variables: { id: bookingId } }],
      onCompleted: () => {
        toast.success('Penalties removed successfully');
        refetchBooking();
      },
      onError: error => {
        toast.error(`Failed to remove penalties: ${error.message}`);
      },
    },
  );

  // Update minimum rental days mutation
  const [updateBookingMinimumRentalDays, { loading: updateMinDaysLoading }] = useMutation(
    UPDATE_BOOKING_MINIMUM_RENTAL_DAYS,
    {
      refetchQueries: [{ query: GET_BOOKING_DETAILS, variables: { id: bookingId } }],
      onCompleted: () => {
        toast.success('Minimum rental days updated successfully');
        setShowMinRentalDaysModal(false);
        setNewMinRentalDays('');
        refetchBooking();
      },
      onError: error => {
        toast.error(`Failed to update minimum rental days: ${error.message}`);
      },
    },
  );

  // Handlers for host actions
  const handleRemoveCancellationPenalty = () => {
    if (!booking?.id) return;
    if (
      window.confirm(
        'This will allow the guest to cancel this booking without any penalty. Continue?',
      )
    ) {
      removeBookingPenalties({
        variables: {
          input: {
            bookingId: booking.id,
            removeCancellationPenalty: true,
          },
        },
      });
    }
  };

  const handleRemoveModificationPenalty = () => {
    if (!booking?.id) return;
    if (
      window.confirm(
        'This will allow the guest to modify this booking without any penalty. Continue?',
      )
    ) {
      removeBookingPenalties({
        variables: {
          input: {
            bookingId: booking.id,
            removeModificationPenalty: true,
          },
        },
      });
    }
  };

  const handleUpdateMinimumRentalDays = () => {
    if (!booking?.minimumRentalDays && booking?.minimumRentalDays !== 0) {
      toast.error('This booking does not have a minimum rental days restriction');
      return;
    }
    setShowMinRentalDaysModal(true);
  };

  const handleConfirmMinRentalDaysUpdate = () => {
    if (!booking?.id) return;
    const numValue = parseInt(newMinRentalDays);
    if (isNaN(numValue) || numValue < 0) {
      toast.error('Please enter a valid number (0 or greater)');
      return;
    }
    if (
      booking?.minimumRentalDays !== null &&
      booking?.minimumRentalDays !== undefined &&
      numValue > booking.minimumRentalDays
    ) {
      toast.error(
        `New minimum rental days (${numValue}) cannot exceed the original minimum rental days (${booking.minimumRentalDays})`,
      );
      return;
    }
    updateBookingMinimumRentalDays({
      variables: {
        input: {
          bookingId: booking.id,
          newMinimumRentalDays: numValue,
        },
      },
    });
  };

  const handleReleaseDeposit = () => {
    if (!booking?.id) return;
    const depositAmount = booking.securityDepositAmount || booking.receipt.securityDeposit || 0;
    if (releaseAmount < 0 || releaseAmount > depositAmount) {
      toast.error('Invalid release amount');
      return;
    }
    releaseDeposit({
      variables: {
        bookingId: booking.id,
        releaseAmount,
        reason: releaseReason.trim() || undefined,
      },
    });
  };

  const [requestAddDriverToBooking, { loading: addingDriver }] = useMutation(
    REQUEST_ADD_ADDITIONAL_DRIVER,
    {
      onCompleted: data => {
        const result = data?.requestAddDriverToBooking;
        if (!result) {
          toast.error('Failed to process additional driver request');
          return;
        }

        if (result.outcome === 'ADDED') {
          toast.success(result.message || 'Driver added successfully');
          setShowAddDriver(false);
          setDriverEmail('');
          refetchBooking();
          return;
        }

        toast.info(result.message);
        setShowAddDriver(false);
        setDriverEmail('');
      },
      onError: (error: Error) => {
        toast.error(`Failed to add driver: ${error.message}`);
      },
    },
  );

  const handleAddDriver = () => {
    if (!driverEmail || !bookingId) return;

    requestAddDriverToBooking({
      variables: {
        input: {
          bookingId,
          driverEmail,
        },
      },
    });
  };

  if (loading) return <BookingDetailsSkeleton />;

  // booking is already extracted above from the appropriate query response

  const timezone = booking?.car?.location?.timezone ?? 'America/Los_Angeles';

  const freeCancellationDeadline = (() => {
    if (!booking?.startAt) return null;
    // Spec 014 Phase 3: the rules this trip was booked under.
    const advanceHours = booking.cancellationRules?.freeCancellationAdvanceHours ?? 24;
    const deadlineMs = booking.startAt - advanceHours * 60 * 60 * 1000;
    return DateTime.fromMillis(deadlineMs).setZone(timezone).toFormat('MMM d, yyyy, h:mm a');
  })();

  const handleContact = async () => {
    if (!booking) {
      return;
    }

    if (!auth.user?.id) {
      toast.info('Please sign in to send messages');
      return;
    }

    if (!booking.guest?.id || !booking.host?.id) {
      toast.error('Chat unavailable: Missing participant information for this booking');
      return;
    }

    const carImageUrl = booking.car?.thumbnailUrl
      ? `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${booking.car.thumbnailUrl}`
      : undefined;

    try {
      const chatId = await chatService.getOrCreateChat({
        bookingId: booking.id,
        guestId: booking.guest.id,
        hostId: booking.host.id,
        carId: booking.car?.id,
        carMake: booking.car?.make || undefined,
        carModel: booking.car?.model || undefined,
        carImageUrl,
      });

      navigate(`/chat/${chatId}`, { state: { bookingId: booking.id } });
    } catch (error: any) {
      toast.error('Unable to start chat. Please try again later.');
    }
  };

  const onCancelTripClicked = () => {
    // 1. generate a refund invoice, BE will handle if the cancellation is free or not
    if (!booking?.id) return toast.error('Booking not found');
    createRefundInvoice({
      variables: {
        bookingId: booking?.id ?? '',
      },
    });
  };

  const handleModifyBooking = (dates: {
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
    startTimestamp: number;
    endTimestamp: number;
  }) => {
    calculateTripExtension({
      variables: {
        input: {
          bookingId: booking?.id ?? '',
          newEndAt: dates.endTimestamp,
        },
      },
    });
  };

  if (!booking || !booking.id || !booking.car || !booking.guest || !booking.receipt)
    return <div>Booking not found</div>;

  const addedAtByDriverId = new Map<string, string | number | undefined>(
    (booking.additionalDrivers ?? []).map(
      (row: { driverId?: string | number; createdAt?: string | number }) => [
        String(row.driverId ?? ''),
        row.createdAt,
      ],
    ),
  );

  const startAtLocal = DateTime.fromMillis(booking.startAt).setZone(timezone);
  const endAtLocal = DateTime.fromMillis(booking.endAt).setZone(timezone);
  const checkinWindowStart = startAtLocal.minus({ hours: 24 });
  const { cancellationReason, cancelledInitiatedAt } = booking;
  /** Use effective app time (dev mock clock) — matches Flutter `appNowMs()` / `booking_details_screen`. */
  const nowLocal = DateTime.fromMillis(appNowMs()).setZone(timezone);
  const isCheckinWindowOpen = nowLocal >= checkinWindowStart;
  /** Start Trip catch-up ends 2h after scheduled return. */
  const isStartTripPastCatchUpDeadline = nowLocal >= endAtLocal.plus({ hours: 2 });
  /** Hide Start Trip only while a swap awaits guest approval; APPROVED/COMPLETED/REJECTED/etc. allow trip again (aligned with Swap Car rules). */
  const isSwapBlocked = booking.swapStatus === CarSwapStatus.PendingGuestApproval;
  const isCheckoutComplete = isHost ? booking.hostFinishedCheckout : booking.guestFinishedCheckout;
  const canShowStartTripButton =
    isCheckinWindowOpen &&
    booking.status === BookingStatus.Booked &&
    !cancelledInitiatedAt &&
    !isSwapBlocked &&
    !isStartTripPastCatchUpDeadline &&
    !isCheckoutComplete;
  const isCheckinComplete = isHost ? booking.hostFinishedCheckin : booking.guestFinishedCheckin;

  // Show "Cancel the Trip" while trip hasn't started OR within 1 hour after trip start.
  // The backend enforces the grace-period && 1h rule; the button just needs to be visible
  // during the window where cancellation might still be allowed.
  const nowMs = appNowMs();
  const tripStartMs = Number(booking.startAt);
  const tripEndMs = Number(booking.endAt);
  const hoursSinceTripStart = (nowMs - tripStartMs) / (1000 * 60 * 60);
  const isWithinPostStartCancelWindow = nowMs <= tripStartMs || hoursSinceTripStart <= 1;
  const postTripActionWindowStartMs = tripEndMs - 3 * 60 * 60 * 1000;
  const postTripActionWindowEndMs = tripEndMs + 90 * 24 * 60 * 60 * 1000;
  const isWithinReturnCheckoutWindow =
    nowMs >= postTripActionWindowStartMs && nowMs <= postTripActionWindowEndMs;
  const isWithinPostTripBillingWindow = nowMs >= tripEndMs && nowMs <= postTripActionWindowEndMs;
  const postTripAllowedStatuses = [
    BookingStatus.Booked,
    BookingStatus.Completed,
    BookingStatus.Shortened,
  ];
  const canShowPostTripReturnButton =
    isWithinReturnCheckoutWindow &&
    postTripAllowedStatuses.includes(booking.status) &&
    !cancelledInitiatedAt &&
    (isHost ? !booking.hostFinishedCheckout : !booking.guestFinishedCheckout);
  const canShowIncidentChargeButton =
    isHost && isWithinPostTripBillingWindow && !cancelledInitiatedAt;
  /** Flutter booking details: primary trip actions (Start / Checkout) live in the bottom bar for host and guest. */
  const fixedBottomVisible =
    (canShowStartTripButton && !isCheckinComplete) || canShowPostTripReturnButton;

  const openActualReturnModal = () => {
    const base = booking.actualReturnAt
      ? DateTime.fromISO(booking.actualReturnAt).setZone(timezone)
      : DateTime.fromMillis(booking.endAt).setZone(timezone);
    setActualReturnValue(base.toFormat("yyyy-MM-dd'T'HH:mm"));
    setIsActualReturnModalOpen(true);
  };

  const handleSaveActualReturn = () => {
    if (!actualReturnValue) {
      toast.error('Select actual return date and time');
      return;
    }
    const actualReturnAt = DateTime.fromFormat(actualReturnValue, "yyyy-MM-dd'T'HH:mm", {
      zone: timezone,
    });
    if (!actualReturnAt.isValid) {
      toast.error('Select a valid actual return date and time');
      return;
    }
    updateActualReturnDateTime({
      variables: {
        input: {
          bookingId: booking.id,
          actualReturnAt: actualReturnAt.toUTC().toISO(),
        },
      },
    });
  };

  return (
    <Container
      style={
        fixedBottomVisible
          ? {
              paddingBottom:
                canShowStartTripButton && !isCheckinComplete && canShowPostTripReturnButton
                  ? '140px'
                  : '80px',
            }
          : undefined
      }
    >
      {/* Mobile Header */}
      <MobileHeader>
        <BookingTitle>
          {!!cancelledInitiatedAt ? 'Cancelled trip' : 'Booked trip'}
          <div tw="flex justify-center mt-1">
            <ShortIdBadge>ID: {formatShortId(booking.shortId || booking.id)}</ShortIdBadge>
          </div>
          {isHost ? (
            <>{booking.guestDisplayName || booking.guest.displayName || 'Deleted User'}'s TRIP</>
          ) : (
            <>
              {booking.hostDisplayName || booking.host?.displayName || 'Deleted User'}'s{' '}
              {booking.car.make}
            </>
          )}
          <DivideLine />
        </BookingTitle>
      </MobileHeader>

      {/* Added DivideLine after MobileHeader */}
      <MobileContent></MobileContent>

      {/* Spec 013 P5: unpaid post-trip charges the guest can pay here. */}
      {isGuestUrl ? (
        <OutstandingCharges bookingId={booking.id} onPaid={() => void refetchBooking()} />
      ) : null}

      {/* Spec 019 W27: the car's handover job on the services site, for the host. */}
      {!isGuestUrl && isHost ? (
        <HandoverJobCard
          trip={{
            id: booking.id,
            carId: booking.carId,
            status: booking.status,
            startAt: booking.startAt,
            hostFinishedCheckin: booking.hostFinishedCheckin,
            timezone,
            handoverJob: handover?.handoverJob,
          }}
          servicesSite={handover?.handoverServicesSite}
        />
      ) : null}

      {/* Top Card - Trip Status & Car Details */}
      <Card>
        {/* Status Banner */}
        {booking.status === BookingStatus.Booked && (
          <StatusBanner status={booking.status}>
            <StatusBannerText>
              <StatusBadgeText>BOOKED</StatusBadgeText>
              {isCheckinComplete && (
                <CheckinLabel>
                  <FontAwesomeIcon icon={faCheckCircle} size="sm" />
                  Check-in Completed
                </CheckinLabel>
              )}
            </StatusBannerText>
          </StatusBanner>
        )}

        {/* Car Info - Two Column Layout */}
        <div tw="flex flex-col md:flex-row gap-6 items-start md:items-center">
          <CarImage
            src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${booking.car?.thumbnailUrl}`}
            alt={`${booking.car.make} ${booking.car.model}`}
          />
          <div tw="flex flex-col gap-2">
            <CarName>{`${booking.car.year} ${booking.car.make.toUpperCase()} ${booking.car.model}`}</CarName>
            <ViewDetails href={`/cars/guest/view/${booking.car.id}`}>View car details</ViewDetails>
          </div>
        </div>
      </Card>

      {/* Trip Details Card */}
      <Card>
        <CardTitle>Trip Details</CardTitle>
        <TwoColumnLayout>
          <Column>
            <Label>Trip Dates</Label>
            <TripDates>
              <div>
                <DateBlock>
                  {!!cancelledInitiatedAt ? (
                    <CancelledDateText>
                      {DateTime.fromMillis(booking.startAt)
                        .setZone(timezone)
                        .toFormat('MM-dd-yyyy')}
                    </CancelledDateText>
                  ) : (
                    <DateText>
                      {DateTime.fromMillis(booking.startAt)
                        .setZone(timezone)
                        .toFormat('MM-dd-yyyy')}
                    </DateText>
                  )}
                  {!!cancelledInitiatedAt ? (
                    <CancelledDateText>
                      {DateTime.fromMillis(booking.startAt).setZone(timezone).toFormat('h:mm a')}
                    </CancelledDateText>
                  ) : (
                    <TimeText>
                      {DateTime.fromMillis(booking.startAt).setZone(timezone).toFormat('h:mm a')}
                    </TimeText>
                  )}
                </DateBlock>
              </div>
              <ArrowIcon>→</ArrowIcon>
              <div>
                <DateBlock>
                  {!!cancelledInitiatedAt ? (
                    <CancelledDateText>
                      {DateTime.fromMillis(booking.endAt).setZone(timezone).toFormat('MM-dd-yyyy')}
                    </CancelledDateText>
                  ) : (
                    <DateText>
                      {DateTime.fromMillis(booking.endAt).setZone(timezone).toFormat('MM-dd-yyyy')}
                    </DateText>
                  )}
                  {!!cancelledInitiatedAt ? (
                    <CancelledDateText>
                      {DateTime.fromMillis(booking.endAt).setZone(timezone).toFormat('h:mm a')}
                    </CancelledDateText>
                  ) : (
                    <TimeText>
                      {DateTime.fromMillis(booking.endAt).setZone(timezone).toFormat('h:mm a')}
                    </TimeText>
                  )}
                </DateBlock>
              </div>
            </TripDates>
          </Column>
          <Column>
            {pickupReturnSameLocation ? (
              <>
                <Label>pick up/return:</Label>
                <LocationAddressLink
                  href={buildGoogleMapsDirectionsUrl(pickupLocationDisplay)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {pickupLocationDisplay}
                </LocationAddressLink>
              </>
            ) : (
              <>
                <Label>Location</Label>
                <div tw="flex flex-col gap-2">
                  <div tw="flex flex-col">
                    <InfoRowLabel>Pickup:</InfoRowLabel>
                    <LocationAddressLink
                      href={buildGoogleMapsDirectionsUrl(pickupLocationDisplay)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pickupLocationDisplay}
                    </LocationAddressLink>
                  </div>
                  <div tw="flex flex-col">
                    <InfoRowLabel>Return:</InfoRowLabel>
                    <LocationAddressLink
                      href={buildGoogleMapsDirectionsUrl(returnLocationDisplay)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {returnLocationDisplay}
                    </LocationAddressLink>
                  </div>
                </div>
              </>
            )}
          </Column>
        </TwoColumnLayout>
        {freeCancellationDeadline &&
          !cancelledInitiatedAt &&
          new Date(booking.startAt) > new Date() && (
            <div
              tw="mt-4 p-3 rounded-lg"
              style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
            >
              <InfoRowLabel>Free cancellation before:</InfoRowLabel>
              <InfoRowValue style={{ color: '#16a34a' }}>{freeCancellationDeadline}</InfoRowValue>
            </div>
          )}
        {isHost && (
          <div
            tw="mt-4 p-3 rounded-lg flex flex-col gap-1"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
            }}
          >
            <InfoRowLabel>Actual Return</InfoRowLabel>
            <InfoRowValue>
              {booking.actualReturnAt
                ? DateTime.fromISO(booking.actualReturnAt)
                    .setZone(timezone)
                    .toFormat('MM-dd-yyyy h:mm a')
                : 'Not recorded'}
              {booking.lateReturnHours
                ? ` (${booking.lateReturnHours} late hour${booking.lateReturnHours === 1 ? '' : 's'})`
                : ''}
            </InfoRowValue>
            {isWithinPostTripBillingWindow && (
              <button
                type="button"
                onClick={openActualReturnModal}
                style={{
                  alignSelf: 'flex-start',
                  fontSize: 13,
                  textDecoration: 'underline',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Edit actual return
              </button>
            )}
          </div>
        )}
      </Card>

      {/* Actions Card */}
      {(canShowStartTripButton ||
        (isHost && !cancelledInitiatedAt && new Date(booking.endAt) > new Date()) ||
        (!isHost && !cancelledInitiatedAt) ||
        !cancelledInitiatedAt ||
        canReportDamageData?.canReportDamage?.canReport) && (
        <Card>
          <CardTitle>Actions</CardTitle>
          <ButtonContainer>
            {canShowIncidentChargeButton && adminOnly('incidentAmounts') && (
              <AdminOnlyNote capability="incidentAmounts" />
            )}
            {canShowIncidentChargeButton &&
              (canHost('incidentAmounts') ? (
                <Button
                  buttonWidth="100%"
                  theme="outlined"
                  text="Charge for Incidents"
                  onClick={() => setIsIncidentChargeModalOpen(true)}
                />
              ) : (
                <ManagedByBrandNote capability="incidentAmounts" />
              ))}
            {/* Host-specific buttons */}
            {isHost && !cancelledInitiatedAt && new Date(booking.endAt) > new Date() && (
              <>
                {canHost('bookingActions') ? (
                  <>
                    {adminOnly('bookingActions') && <AdminOnlyNote capability="bookingActions" />}
                    {!booking.isCancellationPenaltyWaivedByHost && (
                      <Button
                        buttonWidth="100%"
                        theme="outlined"
                        text="Cancel Trip Without Penalty"
                        onClick={handleRemoveCancellationPenalty}
                        isLoading={removePenaltiesLoading}
                        isDisabled={removePenaltiesLoading}
                      />
                    )}
                    {!booking.isShortenTripPenaltyWaivedByHost && (
                      <Button
                        buttonWidth="100%"
                        theme="outlined"
                        text="Modify Trip Without Penalty"
                        onClick={handleRemoveModificationPenalty}
                        isLoading={removePenaltiesLoading}
                        isDisabled={removePenaltiesLoading}
                      />
                    )}
                    {booking.minimumRentalDays !== null &&
                      booking.minimumRentalDays !== undefined && (
                        <Button
                          buttonWidth="100%"
                          theme="outlined"
                          text="Update Minimum Rental Days"
                          onClick={handleUpdateMinimumRentalDays}
                          isLoading={updateMinDaysLoading}
                          isDisabled={updateMinDaysLoading}
                        />
                      )}
                    {booking.status === BookingStatus.Booked &&
                      (!(booking as any).swapStatus ||
                        (booking as any).swapStatus === CarSwapStatus.None ||
                        (booking as any).swapStatus === CarSwapStatus.Rejected ||
                        (booking as any).swapStatus === CarSwapStatus.Expired ||
                        (booking as any).swapStatus === CarSwapStatus.Completed) && (
                        <Button
                          buttonWidth="100%"
                          theme="outlined"
                          text="Swap Car"
                          onClick={() => setIsSwapModalOpen(true)}
                        />
                      )}
                  </>
                ) : (
                  <ManagedByBrandNote capability="bookingActions" />
                )}
                {(booking as any).swapHistory && (booking as any).swapHistory.length > 0 && (
                  <Button
                    buttonWidth="100%"
                    text="View Swap History"
                    theme="outlined"
                    onClick={() => setIsSwapHistoryModalOpen(true)}
                  />
                )}
              </>
            )}
            {/* Contact Guest - always visible to host */}
            {isHost && (
              <Button
                buttonWidth="100%"
                theme="outlined"
                text="Contact Guest"
                onClick={() => setIsContactModalOpen(true)}
              />
            )}
            {/* Guest-specific buttons */}
            {!isHost && (
              <>
                {!cancelledInitiatedAt &&
                  isWithinPostStartCancelWindow &&
                  new Date(booking.endAt) > new Date() && (
                    <Button
                      isLoading={createRefundInvoiceLoading || cancelBookingLoading}
                      theme="outlined"
                      text="Cancel the Trip"
                      onClick={onCancelTripClicked}
                      buttonWidth="100%"
                      isDisabled={
                        createRefundInvoiceLoading ||
                        cancelBookingLoading ||
                        calculateTripExtensionLoading
                      }
                    />
                  )}
                <Button
                  buttonWidth="100%"
                  theme="outlined"
                  text="Modify the Trip"
                  onClick={() => setIsModifyModalOpen(true)}
                  isLoading={calculateTripExtensionLoading}
                  isDisabled={
                    calculateTripExtensionLoading ||
                    (booking as any).swapStatus === CarSwapStatus.PendingGuestApproval
                  }
                />
                <Button
                  buttonWidth="100%"
                  theme="outlined"
                  text="Contact Host"
                  onClick={() => setIsContactModalOpen(true)}
                />
              </>
            )}
            {/* Rating button - shows 3 hours before trip ends (matching backend canRateBooking logic) */}
            {!cancelledInitiatedAt && (
              <RateTripButton
                bookingId={bookingId!}
                isHost={isHost}
                hostName={booking.host?.displayName || booking.host?.firstName || 'Host'}
                hostAvatar={undefined}
                guestName={booking.guest?.displayName || booking.guest?.firstName || 'Guest'}
                guestAvatar={undefined}
                carName={`${booking.car?.year} ${booking.car?.make} ${booking.car?.model}`}
                tripDates={`${DateTime.fromMillis(booking.startAt).toFormat('MMM d')} - ${DateTime.fromMillis(booking.endAt).toFormat('MMM d')}`}
                onRatingSubmitted={() => refetchBooking()}
                alreadyRated={isHost ? false : !!booking.isReviewed}
              />
            )}
            {/* Report Damage button - shows based on canReportDamage query */}
            {!cancelledInitiatedAt && canReportDamageData?.canReportDamage?.canReport && (
              <Button
                buttonWidth="100%"
                text="Report Damage"
                theme="outlined"
                onClick={() => setIsDamageClaimModalOpen(true)}
              />
            )}
          </ButtonContainer>
        </Card>
      )}

      {/* Host swap pending status display */}
      {isHost && (booking as any).swapStatus === CarSwapStatus.PendingGuestApproval && (
        <SwapRequestBanner>
          <SwapRequestTitle>Car Swap Pending Guest Approval</SwapRequestTitle>
          <SwapRequestText>
            You have requested to swap this car. Waiting for guest approval.
            {(booking as any).swapNote && (
              <>
                <br />
                <strong>Your note:</strong> {(booking as any).swapNote}
              </>
            )}
            {swapCarData?.car && (
              <>
                <br />
                <strong>Requested Car:</strong>{' '}
                {`${swapCarData.car.year} ${swapCarData.car.make} ${swapCarData.car.model}`}
              </>
            )}
            {(booking as any).swapResponseDeadline && (
              <>
                <br />
                <strong>Response deadline:</strong>{' '}
                {DateTime.fromJSDate(new Date((booking as any).swapResponseDeadline))
                  .setZone(booking.car?.location?.timezone || 'America/Los_Angeles')
                  .toFormat('MMM dd, yyyy h:mm a')}
              </>
            )}
            {(booking as any).swapStartTime && (
              <>
                <br />
                <strong>Swap Start Time:</strong>{' '}
                {DateTime.fromMillis((booking as any).swapStartTime)
                  .setZone(booking.car?.location?.timezone || 'America/Los_Angeles')
                  .toFormat('MMM dd, yyyy h:mm a')}
              </>
            )}
          </SwapRequestText>
          <SwapRequestActions>
            {canHost('bookingActions') ? (
              <>
                <Button
                  text="Force Approve Swap"
                  onClick={handleForceApproveSwap}
                  isLoading={forceApproveSwapLoading}
                  isDisabled={forceApproveSwapLoading || forceDeclineSwapLoading}
                  buttonWidth="200px"
                />
                <Button
                  text="Force Decline Swap"
                  theme="outlined"
                  onClick={handleForceDeclineSwap}
                  isLoading={forceDeclineSwapLoading}
                  isDisabled={forceApproveSwapLoading || forceDeclineSwapLoading}
                  buttonWidth="200px"
                />
              </>
            ) : (
              <ManagedByBrandNote capability="bookingActions" />
            )}
          </SwapRequestActions>
        </SwapRequestBanner>
      )}

      {/* Guest swap request display */}
      {!isHost && (booking as any).swapStatus === CarSwapStatus.PendingGuestApproval && (
        <SwapRequestBanner>
          <SwapRequestTitle>Car Swap Request</SwapRequestTitle>
          <SwapRequestText>
            The host has requested to swap your car for this booking.
            {(booking as any).swapNote && (
              <>
                <br />
                <strong>Note from host:</strong> {(booking as any).swapNote}
              </>
            )}
            {(booking as any).swapResponseDeadline && (
              <>
                <br />
                <strong>Response deadline:</strong>{' '}
                {DateTime.fromJSDate(new Date((booking as any).swapResponseDeadline))
                  .setZone(booking.car?.location?.timezone || 'America/Los_Angeles')
                  .toFormat('MMM dd, yyyy h:mm a')}
              </>
            )}
          </SwapRequestText>
          <SwapRequestActions>
            <Button
              text="Approve Swap"
              onClick={handleApproveSwap}
              isLoading={approveSwapLoading}
              isDisabled={approveSwapLoading || rejectSwapLoading}
              buttonWidth="200px"
            />
            <Button
              text="Reject Swap"
              onClick={handleRejectSwap}
              theme="outlined"
              isLoading={rejectSwapLoading}
              isDisabled={approveSwapLoading || rejectSwapLoading}
              buttonWidth="200px"
            />
          </SwapRequestActions>
        </SwapRequestBanner>
      )}

      {/* Spec 015: a trip cancelled because the car was unlisted for a safety recall. */}
      {unlistedCancellationText(cancellationReason, isHost ? 'host' : 'guest') && (
        <Alert tone="info" title="Safety recall" data-testid="safety-recall-cancellation">
          {unlistedCancellationText(cancellationReason, isHost ? 'host' : 'guest')}
        </Alert>
      )}

      {/* Refund Section - shown for cancelled bookings */}
      {cancellationReason &&
        booking.invoices &&
        booking.invoices.length > 0 &&
        (() => {
          // Find refund invoices for trip refunds (exclude deposit refunds)
          const refundInvoices = booking.invoices
            .filter((inv: any) => Math.abs(inv.amount ?? 0) > 0.01)
            .filter((inv: any) => {
              const type = inv.type?.toUpperCase?.() ?? '';
              return type.includes('REFUND') && type !== 'DEPOSIT_REFUND';
            });
          if (refundInvoices.length === 0) return null;

          const sortedRefunds = [...refundInvoices].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
          // Get the most recent refund invoice
          const latestRefund = sortedRefunds[0];
          // Calculate total refund amount (sum of all refund invoice amounts - they're negative)
          const totalRefundAmount = refundInvoices.reduce(
            (sum: number, inv: any) => sum + Math.abs(inv.amount),
            0,
          );
          const splitRefunds = sortedRefunds.filter((inv: any) => inv.originalInvoiceId);
          const isSplitRefund = splitRefunds.length > 1;

          const getRefundStatusColor = (status: string | null | undefined) => {
            const statusLower = status?.toLowerCase();
            switch (statusLower) {
              case 'completed':
                return Colors.Success;
              case 'processing':
                return Colors.Info;
              case 'pending_host_review':
                return Colors.Warning;
              case 'failed':
              case 'cancelled':
                return Colors.Error;
              default:
                return Colors.Gray500;
            }
          };

          const formatRefundStatus = (status: string | null | undefined) => {
            if (!status) return 'N/A';
            const statusLower = status.toLowerCase();
            // Make status clearer for guests - "completed" means Stripe processed it,
            // but funds take 5-10 business days to appear on card
            if (statusLower === 'completed') return 'REFUND PROCESSED';
            if (statusLower === 'processing') return 'PROCESSING';
            if (statusLower === 'pending_host_review') return 'PENDING REVIEW';
            if (statusLower === 'failed') return 'FAILED';
            if (statusLower === 'cancelled') return 'CANCELLED';
            return status.replace(/_/g, ' ').toUpperCase();
          };

          const hasMeaningfulStatus = (status?: string | null) => {
            if (!status) return false;
            return status.toLowerCase() !== 'none';
          };

          return (
            <RefundSection>
              <SectionTitle>Refund Details</SectionTitle>

              <DepositRow>
                <DepositLabel>Total Refund:</DepositLabel>
                <DepositValue>${totalRefundAmount.toFixed(2)}</DepositValue>
              </DepositRow>

              {hasMeaningfulStatus(latestRefund.refundStatus) && (
                <>
                  <DepositRow>
                    <DepositLabel>Status:</DepositLabel>
                    <DepositValue>
                      <StatusBadge color={getRefundStatusColor(latestRefund.refundStatus)}>
                        {formatRefundStatus(latestRefund.refundStatus)}
                      </StatusBadge>
                      {/* Only show refresh button for PROCESSING status - completed/failed won't change */}
                      {latestRefund.refundStatus?.toLowerCase() === 'processing' && (
                        <RefreshLink
                          onClick={() =>
                            refreshRefundStatus({ variables: { invoiceId: latestRefund.id } })
                          }
                          disabled={refreshingRefundStatus}
                        >
                          {refreshingRefundStatus ? 'Checking...' : 'Check status'}
                        </RefreshLink>
                      )}
                    </DepositValue>
                  </DepositRow>
                  {(['completed', 'COMPLETED'] as string[]).includes(
                    latestRefund.refundStatus as string,
                  ) && (
                    <DepositRow>
                      <DepositLabel></DepositLabel>
                      <DepositValue
                        style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}
                      >
                        Allow 5-10 business days for funds to appear on card
                      </DepositValue>
                    </DepositRow>
                  )}
                </>
              )}

              {isSplitRefund && (
                <>
                  <DepositRow>
                    <DepositLabel>Split refunds:</DepositLabel>
                    <DepositValue>{splitRefunds.length} payments</DepositValue>
                  </DepositRow>
                  {splitRefunds.map((refund: any, index: number) => (
                    <DepositRow key={refund.id}>
                      <DepositLabel>Refund {index + 1}:</DepositLabel>
                      <DepositValue>
                        ${Math.abs(refund.amount ?? 0).toFixed(2)}
                        {hasMeaningfulStatus(refund.refundStatus)
                          ? ` • ${formatRefundStatus(refund.refundStatus)}`
                          : ''}
                      </DepositValue>
                    </DepositRow>
                  ))}
                </>
              )}

              {latestRefund.refundAmount !== null && latestRefund.refundAmount !== undefined && (
                <DepositRow>
                  <DepositLabel>Refund Amount (Stripe):</DepositLabel>
                  <DepositValue>${Math.abs(latestRefund.refundAmount).toFixed(2)}</DepositValue>
                </DepositRow>
              )}

              {latestRefund.refundInitiatedAt && (
                <DepositRow>
                  <DepositLabel>Refund Initiated:</DepositLabel>
                  <DepositValue>
                    {DateTime.fromISO(latestRefund.refundInitiatedAt).toFormat(
                      'MMM dd, yyyy HH:mm',
                    )}
                  </DepositValue>
                </DepositRow>
              )}

              {latestRefund.refundCompletedAt && (
                <DepositRow>
                  <DepositLabel>Refund Completed:</DepositLabel>
                  <DepositValue>
                    {DateTime.fromISO(latestRefund.refundCompletedAt).toFormat(
                      'MMM dd, yyyy HH:mm',
                    )}
                  </DepositValue>
                </DepositRow>
              )}

              {cancelledInitiatedAt && (
                <DepositRow>
                  <DepositLabel>Cancellation Initiated:</DepositLabel>
                  <DepositValue>
                    {DateTime.fromISO(String(cancelledInitiatedAt)).toFormat('MMM dd, yyyy HH:mm')}
                  </DepositValue>
                </DepositRow>
              )}

              {isHost && (
                <>
                  {latestRefund.hostApprovedRefund !== null &&
                    latestRefund.hostApprovedRefund !== undefined && (
                      <DepositRow>
                        <DepositLabel>Host Approved:</DepositLabel>
                        <DepositValue>
                          <StatusBadge
                            color={
                              latestRefund.hostApprovedRefund ? Colors.Success : Colors.Warning
                            }
                          >
                            {latestRefund.hostApprovedRefund ? 'YES' : 'PENDING'}
                          </StatusBadge>
                        </DepositValue>
                      </DepositRow>
                    )}

                  {latestRefund.hostRefundReviewedAt && (
                    <DepositRow>
                      <DepositLabel>Host Reviewed At:</DepositLabel>
                      <DepositValue>
                        {DateTime.fromISO(latestRefund.hostRefundReviewedAt).toFormat(
                          'MMM dd, yyyy HH:mm',
                        )}
                      </DepositValue>
                    </DepositRow>
                  )}

                  {booking.receipt.cancellationPenalty !== null &&
                    booking.receipt.cancellationPenalty !== undefined &&
                    booking.receipt.cancellationPenalty > 0 && (
                      <DepositRow>
                        <DepositLabel>Penalty Amount:</DepositLabel>
                        <DepositValue>
                          ${booking.receipt.cancellationPenalty.toFixed(2)}
                        </DepositValue>
                      </DepositRow>
                    )}
                </>
              )}

              {latestRefund.note && (
                <DepositRow>
                  <DepositLabel>Note:</DepositLabel>
                  <DepositValue>{latestRefund.note}</DepositValue>
                </DepositRow>
              )}
            </RefundSection>
          );
        })()}

      {/* Financials & Protection Card */}
      <Card>
        <CardTitle>Financials & Protection</CardTitle>
        <CardContent>
          {/* Protection Plan */}
          <ProtectionPlanDetails booking={booking} />

          {/* Receipt */}
          <div>
            {mileageIncludedDisplay && (
              <InfoRow>
                <InfoRowLeft>
                  <InfoRowLabel>Mileage included:</InfoRowLabel>
                  <SubText>{mileageIncludedDisplay.note}</SubText>
                </InfoRowLeft>
                <InfoRowValue>{mileageIncludedDisplay.value}</InfoRowValue>
              </InfoRow>
            )}
            <InfoRow>
              <InfoRowLeft>
                <InfoRowLabel>Total {isHost ? 'Earnings' : 'Price'}:</InfoRowLabel>
              </InfoRowLeft>
              <InfoRowValue>${receiptDisplayTotal.toFixed(2)}</InfoRowValue>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoRowLabel>Card Number:</InfoRowLabel>
              </InfoRowLeft>
              <InfoRowValue>
                xxxx-xxxx-xxxx-{booking.receipt.last4digitOfCard ?? 'unknown'}
              </InfoRowValue>
            </InfoRow>
            <DetailLink
              onClick={showReceiptModal}
              style={{ marginTop: '8px', display: 'inline-block' }}
            >
              View Receipt Details
            </DetailLink>
          </div>
        </CardContent>
      </Card>

      {/* Security Deposit Section (Keep for host actions) */}
      {(booking.securityDepositAmount || booking.receipt.securityDeposit) && (
        <SecurityDepositSection>
          <SectionTitle>Security Deposit</SectionTitle>

          <DepositRow>
            <DepositLabel>Deposit Amount:</DepositLabel>
            <DepositValue>
              ${(booking.securityDepositAmount || booking.receipt.securityDeposit || 0).toFixed(2)}
            </DepositValue>
          </DepositRow>

          {booking.securityDepositStatus && (
            <DepositRow>
              <DepositLabel>Status:</DepositLabel>
              <DepositValue>
                <StatusBadge
                  color={
                    booking.securityDepositStatus === 'PENDING_HOLD'
                      ? Colors.Warning
                      : booking.securityDepositStatus === 'HELD'
                        ? Colors.Error
                        : booking.securityDepositStatus === 'RELEASED'
                          ? Colors.Success
                          : Colors.Info
                  }
                >
                  {booking.securityDepositStatus.replace('_', ' ')}
                </StatusBadge>
              </DepositValue>
            </DepositRow>
          )}

          {booking.securityDepositCardType && (
            <DepositRow>
              <DepositLabel>Card Type:</DepositLabel>
              <DepositValue>{booking.securityDepositCardType}</DepositValue>
            </DepositRow>
          )}

          {booking.securityDepositHeldUntil && (
            <DepositRow>
              <DepositLabel>Auto-release:</DepositLabel>
              <DepositValue>
                {DateTime.fromISO(booking.securityDepositHeldUntil).toFormat('MMM dd, yyyy HH:mm')}
              </DepositValue>
            </DepositRow>
          )}

          {booking.securityDepositHoldReason && (
            <DepositRow>
              <DepositLabel>Hold reason:</DepositLabel>
              <DepositValue>{booking.securityDepositHoldReason}</DepositValue>
            </DepositRow>
          )}

          {booking.securityDepositReleaseReason && (
            <DepositRow>
              <DepositLabel>Release reason:</DepositLabel>
              <DepositValue>{booking.securityDepositReleaseReason}</DepositValue>
            </DepositRow>
          )}

          {/* Action Buttons - Only for Host */}
          {isHost && booking.securityDepositStatus === 'PENDING_HOLD' && (
            <>
              <DepositDivider />
              <DepositButtonContainer>
                {!booking.securityDepositHoldRequested && (
                  <Button
                    text="Hold Deposit"
                    onClick={() => {
                      setHoldReason('');
                      setIsHoldModalOpen(true);
                    }}
                    buttonWidth="200px"
                    isDisabled={holdingDeposit}
                    isLoading={holdingDeposit}
                  />
                )}
                <Button
                  text="Release Deposit"
                  onClick={() => {
                    setReleaseAmount(0);
                    setReleaseReason('');
                    setIsReleaseModalOpen(true);
                  }}
                  buttonWidth="200px"
                  isDisabled={releasingDeposit}
                />
              </DepositButtonContainer>
            </>
          )}
        </SecurityDepositSection>
      )}

      {/* Damage Claims Section */}
      {damageClaims.length > 0 && (
        <DamageClaimsSection>
          <SectionTitle>Damage Claims</SectionTitle>
          {damageClaims.map((claim: any) => {
            const damageClaimDetailPath = isHost
              ? `/cars/host/damage-claims/${claim.id}`
              : `/guest/damage-claims/${claim.id}`;
            return (
              <ClaimCardLink key={claim.id} to={damageClaimDetailPath}>
                <DepositRow>
                  <DepositLabel>Claim Number:</DepositLabel>
                  <DepositValue>
                    <ClaimNumHighlight>#{claim.claimNum}</ClaimNumHighlight>
                  </DepositValue>
                </DepositRow>
                <DepositRow>
                  <DepositLabel>Status:</DepositLabel>
                  <DepositValue>
                    <StatusBadge
                      color={
                        claim.status === 'PENDING'
                          ? Colors.Warning
                          : claim.status === 'UNDER_REVIEW'
                            ? Colors.Info
                            : claim.status === 'APPROVED' || claim.status === 'PARTIALLY_APPROVED'
                              ? Colors.Success
                              : claim.status === 'REJECTED'
                                ? Colors.Error
                                : Colors.Gray500
                      }
                    >
                      {claim.status?.replace(/_/g, ' ')}
                    </StatusBadge>
                  </DepositValue>
                </DepositRow>
                <DepositRow>
                  <DepositLabel>Type:</DepositLabel>
                  <DepositValue>
                    {claim.damageType === 'PHYSICAL' ? 'Physical Damage' : 'Mechanical Issue'}
                  </DepositValue>
                </DepositRow>
                {claim.claimAmount && (
                  <DepositRow>
                    <DepositLabel>Claim Amount:</DepositLabel>
                    <DepositValue>${claim.claimAmount.toFixed(2)}</DepositValue>
                  </DepositRow>
                )}
              </ClaimCardLink>
            );
          })}
        </DamageClaimsSection>
      )}

      {/* DL Verification Section (spec 014a: the brand may review licences itself) */}
      {isHost && adminOnly('licenceReview') && <AdminOnlyNote capability="licenceReview" />}
      {isHost && !canHost('licenceReview') ? (
        <ManagedByBrandNote capability="licenceReview" />
      ) : (
        <DLVerification
          bookingId={booking.id}
          isHost={isHost}
          dlVerificationStatus={booking.dlVerificationStatus}
          dlPhoto={booking.dlPhoto}
          dlSelfiePhoto={booking.dlSelfiePhoto}
          dlPhotoUrl={booking.dlPhotoUrl}
          dlSelfiePhotoUrl={booking.dlSelfiePhotoUrl}
          dlRejectionReason={booking.dlRejectionReason}
          dlSubmittedAt={booking.dlSubmittedAt}
          dlVerifiedAt={booking.dlVerifiedAt}
          tripStartTime={booking.startAt}
          totalTripAmount={booking.receipt?.totalPrice || 0}
          onUpdate={refetchBooking}
          autoOpenVerify={autoOpenDLVerify}
          onAutoOpenHandled={() => setAutoOpenDLVerify(false)}
        />
      )}

      {/* Trip Photos Card */}
      <Card>
        <CardTitle>
          TRIP PHOTOS
          {booking.tripStartImages && booking.tripEndImages && (
            <span tw="font-normal text-text-secondary ml-2">
              ({(booking.tripStartImages?.length || 0) + (booking.tripEndImages?.length || 0)})
            </span>
          )}
        </CardTitle>
        <DetailLink
          onClick={() => setIsTripPhotosModalOpen(true)}
          style={{ cursor: 'pointer', display: 'inline-block' }}
        >
          View and add more
        </DetailLink>
      </Card>

      {/* Only show Add Additional Driver for guests and before trip ends */}
      {!isHost && new Date(booking.endAt) > new Date() && (
        <AddDriverSection>
          <Button
            text="Add Additional Driver"
            onClick={() => setShowAddDriver(true)}
            buttonWidth="100%"
          />
        </AddDriverSection>
      )}

      {/* Vehicle Documents Card */}
      <Card>
        <CardTitle>VEHICLE DOCUMENTS</CardTitle>
        <DetailLink
          onClick={() => navigate(`/agreement/${booking.id}`)}
          style={{ cursor: 'pointer', display: 'inline-block' }}
        >
          Car sharing agreement
        </DetailLink>
      </Card>

      {/* People Card */}
      <Card>
        <CardTitle>People</CardTitle>
        <PeopleSection>
          {/* Guest Information */}
          <PersonInfo>
            <Label>Guest Information</Label>
            <PersonName>{booking.guest.displayName || booking.guest.firstName || 'N/A'}</PersonName>
            {booking.guest.email && <PersonEmail>{booking.guest.email}</PersonEmail>}
          </PersonInfo>

          {/* Host Information */}
          {booking.host?.displayName && (
            <PersonInfo>
              <Label>Host Information</Label>
              <PersonName>{booking.host.displayName}</PersonName>
              {booking.host.email && <PersonEmail>{booking.host.email}</PersonEmail>}
            </PersonInfo>
          )}

          {(booking.additionalDriverDetails ?? []).length > 0 && (
            <PersonInfo>
              <Label>Additional drivers</Label>
              {(booking.additionalDriverDetails ?? []).map((driver: IUser) => {
                const name =
                  [driver.firstName, driver.lastName].filter(Boolean).join(' ').trim() || 'Unknown';
                const addedRaw = addedAtByDriverId.get(String(driver.id));
                return (
                  <div key={driver.id}>
                    <PersonName>{name}</PersonName>
                    {driver.email && <PersonEmail>{driver.email}</PersonEmail>}
                    <PersonMeta>
                      Added (vehicle local time): {formatInstantInCarTimezone(addedRaw, timezone)}
                    </PersonMeta>
                  </div>
                );
              })}
            </PersonInfo>
          )}
        </PeopleSection>
      </Card>

      {/* Timeline Card */}
      <Card>
        <BookingActivityTimeline
          activities={
            bookingActivitiesData?.getBookingActivities ?? (booking as any).activities ?? []
          }
          timezone={booking.car?.location?.timezone ?? undefined}
        />
      </Card>

      {/* Update Minimum Rental Days Modal */}
      <Modal
        isOpen={showMinRentalDaysModal}
        onClose={() => {
          setShowMinRentalDaysModal(false);
          setNewMinRentalDays('');
        }}
        title="Update Minimum Rental Days"
      >
        <ReleaseModalContent>
          <p tw="text-text-secondary mb-4">
            Enter the new minimum rental days for this booking. This value must be less than or
            equal to the original minimum rental days ({booking?.minimumRentalDays ?? 'N/A'} days).
          </p>

          <div>
            <NumericInput
              value={newMinRentalDays ? parseInt(newMinRentalDays) : null}
              onValueChange={(value: number | null) => setNewMinRentalDays(value?.toString() ?? '')}
              minValue={0}
              maxValue={booking?.minimumRentalDays ?? undefined}
              placeholder="0"
              width="100%"
            />
            <ReleaseHelperText>
              Current: {booking?.minimumRentalDays ?? 'None'} days{'\n'}
              New value must be ≤ {booking?.minimumRentalDays ?? 'N/A'} days
            </ReleaseHelperText>
          </div>

          <div tw="flex gap-4 mt-4 justify-center">
            <Button
              text="Cancel"
              onClick={() => {
                setShowMinRentalDaysModal(false);
                setNewMinRentalDays('');
              }}
              buttonWidth="150px"
            />
            <Button
              text="Confirm"
              onClick={handleConfirmMinRentalDaysUpdate}
              buttonWidth="150px"
              isDisabled={updateMinDaysLoading}
              isLoading={updateMinDaysLoading}
            />
          </div>
        </ReleaseModalContent>
      </Modal>

      <ModifyBookingModal
        isOpen={isModifyModalOpen}
        onClose={() => setIsModifyModalOpen(false)}
        onConfirm={handleModifyBooking}
        initialStartDate={calendarJsDateFromMillisInZone(booking.startAt, timezone)}
        initialEndDate={calendarJsDateFromMillisInZone(booking.endAt, timezone)}
        initialStartTime={convert24To12Hour(clockTimeInZone(booking.startAt, timezone))}
        initialEndTime={convert24To12Hour(clockTimeInZone(booking.endAt, timezone))}
        carId={booking.car.id}
        excludeBookingId={booking.id}
        timezone={timezone}
      />
      <ReceiptDetailsModal
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        booking={booking}
        distanceUnit={bookingLocale.distanceUnit}
        currency={bookingLocale.currency}
        taxLabel={bookingLocale.taxLabel}
      />
      <StartTripCheckinModal
        isOpen={isStartTripModalOpen}
        onClose={() => setIsStartTripModalOpen(false)}
        booking={booking}
        isHost={isHost}
        refetchBooking={refetchBooking}
        onCompleted={() => refetchBooking()}
      />
      <TripPhotosModal
        isOpen={isTripPhotosModalOpen}
        onClose={() => setIsTripPhotosModalOpen(false)}
        booking={booking}
        refetchBooking={refetchBooking}
        isHost={isHost}
      />
      <PostTripReturnModal
        isOpen={isPostTripReturnModalOpen}
        onClose={() => setIsPostTripReturnModalOpen(false)}
        booking={booking}
        isHost={isHost}
        refetchBooking={async () => {
          await refetchBooking();
          await refetchBookingActivities();
        }}
      />
      {isHost && (
        <IncidentChargeModal
          isOpen={isIncidentChargeModalOpen}
          onClose={() => setIsIncidentChargeModalOpen(false)}
          booking={booking}
          refetchBooking={async () => {
            await refetchBooking();
            await refetchBookingActivities();
          }}
        />
      )}
      <ExtendTripPaymentModal
        bookingId={bookingId || ''}
        paymentMethodId={booking.defaultPaymentMethodId || ''}
        amount={modifyCost}
        shortenTripPenalty={shortenTripPenalty}
        freeCancellationServiceFee={freeCancellationServiceFee}
        newEndAt={modifyEndAt}
        originalEndAt={booking.endAt}
        timezone={timezone}
        isExtendTripPaymentModalOpen={isExtendTripPaymentModalOpen}
        refetchBooking={refetchBooking}
        refetchBookingActivities={refetchBookingActivities}
        onClose={() => {
          setIsExtendTripPaymentModalOpen(false);
          setModifyCost(0);
          setModifyEndAt(0);
        }}
      />

      <Modal
        modalHeight="auto"
        modalWidth="min(400px, calc(100vw - 2rem))"
        isOpen={showAddDriver}
        onClose={() => {
          setShowAddDriver(false);
          setDriverEmail('');
        }}
        title="Add Additional Driver"
      >
        <AddDriverModalContent>
          <DriverInput
            type="email"
            placeholder="Enter driver's email"
            value={driverEmail}
            onChange={e => setDriverEmail(e.target.value)}
          />
          <Button
            buttonWidth="100%"
            text={addingDriver ? 'Adding...' : 'Add Driver'}
            onClick={handleAddDriver}
            isDisabled={addingDriver || !driverEmail}
          />
          <Button
            text="Cancel"
            onClick={() => {
              setShowAddDriver(false);
              setDriverEmail('');
            }}
          />
        </AddDriverModalContent>
      </Modal>

      {/* Hold Deposit Modal */}
      <Modal
        modalHeight="auto"
        modalWidth="min(400px, calc(100vw - 2rem))"
        isOpen={isHoldModalOpen}
        onClose={() => {
          setIsHoldModalOpen(false);
          setHoldReason('');
        }}
        title="Hold Security Deposit"
      >
        <ReleaseModalContent>
          <p tw="text-text-secondary mb-4">Add an optional reason for holding the deposit.</p>

          <div>
            <label tw="block text-sm font-medium text-text-secondary mb-1">Reason (optional)</label>
            <ReasonInput
              value={holdReason}
              onChange={e => setHoldReason(e.target.value)}
              placeholder="Add a short note for the guest or internal records"
            />
          </div>

          <DepositButtonContainer>
            <Button
              text="Cancel"
              onClick={() => {
                setIsHoldModalOpen(false);
                setHoldReason('');
              }}
              buttonWidth="150px"
            />
            <Button
              text="Hold"
              onClick={handleHoldDeposit}
              buttonWidth="150px"
              isDisabled={holdingDeposit}
              isLoading={holdingDeposit}
            />
          </DepositButtonContainer>
        </ReleaseModalContent>
      </Modal>

      {/* Release Deposit Modal */}
      <Modal
        modalHeight="auto"
        modalWidth="min(400px, calc(100vw - 2rem))"
        isOpen={isReleaseModalOpen}
        onClose={() => {
          setIsReleaseModalOpen(false);
          setReleaseAmount(0);
          setReleaseReason('');
        }}
        title="Release Security Deposit"
      >
        <ReleaseModalContent>
          <p tw="text-text-secondary mb-4">
            Enter the amount to release. The remaining amount will be charged.
          </p>

          <div>
            <label tw="block text-sm font-medium text-text-secondary mb-1">
              Release Amount ($)
            </label>
            <NumericInput
              value={releaseAmount}
              onValueChange={(value: number | null) => setReleaseAmount(value ?? 0)}
              minValue={0}
              maxValue={booking.securityDepositAmount || booking.receipt.securityDeposit || 0}
              placeholder="0"
              width="100%"
            />
            <ReleaseHelperText>
              Deposit: $
              {(booking.securityDepositAmount || booking.receipt.securityDeposit || 0).toFixed(2)} |
              Will charge: $
              {(
                (booking.securityDepositAmount || booking.receipt.securityDeposit || 0) -
                releaseAmount
              ).toFixed(2)}
            </ReleaseHelperText>
          </div>

          <div>
            <label tw="block text-sm font-medium text-text-secondary mb-1">Reason (optional)</label>
            <ReasonInput
              value={releaseReason}
              onChange={e => setReleaseReason(e.target.value)}
              placeholder="Add a short note for the guest or internal records"
            />
          </div>

          <DepositButtonContainer>
            <Button
              text="Cancel"
              onClick={() => {
                setIsReleaseModalOpen(false);
                setReleaseAmount(0);
                setReleaseReason('');
              }}
              buttonWidth="150px"
            />
            <Button
              text="Release"
              onClick={handleReleaseDeposit}
              buttonWidth="150px"
              isDisabled={releasingDeposit}
              isLoading={releasingDeposit}
            />
          </DepositButtonContainer>
        </ReleaseModalContent>
      </Modal>
      <Modal
        modalHeight="auto"
        modalWidth="min(400px, calc(100vw - 2rem))"
        isOpen={isActualReturnModalOpen}
        onClose={() => setIsActualReturnModalOpen(false)}
        title="Actual Return"
      >
        <ReleaseModalContent>
          <p tw="text-text-secondary mb-4">
            Record the actual date and time the vehicle was returned.
          </p>
          <div>
            <label tw="block text-sm font-medium text-text-secondary mb-1">
              Actual return date/time
            </label>
            <DriverInput
              type="datetime-local"
              value={actualReturnValue}
              onChange={e => setActualReturnValue(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <DepositButtonContainer>
            <Button
              text="Cancel"
              theme="outlined"
              onClick={() => setIsActualReturnModalOpen(false)}
              buttonWidth="150px"
            />
            <Button
              text={updatingActualReturn ? 'Saving...' : 'Save'}
              onClick={handleSaveActualReturn}
              buttonWidth="150px"
              isDisabled={updatingActualReturn}
              isLoading={updatingActualReturn}
            />
          </DepositButtonContainer>
        </ReleaseModalContent>
      </Modal>
      {/* Car Swap Modal */}
      {booking && (
        <CarSwapModal
          isOpen={isSwapModalOpen}
          onClose={() => setIsSwapModalOpen(false)}
          booking={booking}
          onSwapRequested={() => {
            setIsSwapModalOpen(false);
            refetchBooking();
            refetchBookingActivities();
          }}
        />
      )}
      {/* Swap History Modal */}
      {booking && (
        <SwapHistoryModal
          isOpen={isSwapHistoryModalOpen}
          onClose={() => setIsSwapHistoryModalOpen(false)}
          booking={booking}
        />
      )}
      {/* Damage Claim Modal */}
      {booking && (
        <DamageClaimModal
          isOpen={isDamageClaimModalOpen}
          onClose={() => setIsDamageClaimModalOpen(false)}
          bookingId={booking.id}
          guestName={booking.guest?.displayName || booking.guest?.firstName || 'Guest'}
          tripStartImages={booking.tripStartImages}
          tripEndImages={booking.tripEndImages}
          onClaimSubmitted={() => {
            setIsDamageClaimModalOpen(false);
            refetchBooking();
            void refetchDamageClaimsByBooking();
          }}
        />
      )}
      {/* Fixed Bottom Post-Trip / Start Trip Bar */}
      {fixedBottomVisible && (
        <FixedBottomBar>
          <FixedBottomBarInner style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {canShowStartTripButton && !isCheckinComplete && (
              <Button
                buttonWidth="100%"
                text="Start Trip"
                onClick={() => setIsStartTripModalOpen(true)}
              />
            )}
            {canShowPostTripReturnButton && (
              <Button
                buttonWidth="100%"
                text={isHost ? 'Checkout Guest' : 'End Trip'}
                onClick={() => setIsPostTripReturnModalOpen(true)}
              />
            )}
          </FixedBottomBarInner>
        </FixedBottomBar>
      )}

      {/* Contact Modal */}
      <Modal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        modalHeight="auto"
        modalWidth="min(360px, calc(100vw - 2rem))"
        contentAlign="center"
      >
        <ContactModalContent>
          <ContactModalTitle>
            {isHost
              ? `Contact ${booking.guest?.displayName || booking.guest?.firstName || 'Guest'}`
              : `Contact ${booking.host?.displayName || booking.host?.firstName || 'Host'}`}
          </ContactModalTitle>
          {(isHost ? booking.guest?.phoneNumber : booking.host?.phoneNumber) && (
            <>
              <ContactModalItem
                href={`tel:${isHost ? booking.guest?.phoneNumber : booking.host?.phoneNumber}`}
              >
                <Button
                  buttonWidth="100%"
                  theme="outlined"
                  text={isHost ? 'Call Guest' : 'Call Host'}
                />
              </ContactModalItem>
              <ContactModalItem
                href={`sms:${isHost ? booking.guest?.phoneNumber : booking.host?.phoneNumber}`}
              >
                <Button
                  buttonWidth="100%"
                  theme="outlined"
                  text={isHost ? 'Text Guest' : 'Text Host'}
                />
              </ContactModalItem>
            </>
          )}
          <Button
            buttonWidth="100%"
            theme="outlined"
            text="Chat in App"
            onClick={() => {
              setIsContactModalOpen(false);
              handleContact();
            }}
          />
        </ContactModalContent>
      </Modal>
    </Container>
  );
}
